const normalize = size => String(size).trim().toUpperCase().replace(/^XXXL$/, '3XL').replace(/^2XL$/, 'XXL');
const stripUuid = name => String(name || '').replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/ig, '');
export function sizeAvailability(variants, selected, position, size) {
  const matches = variants.filter(v => v.options.every((value, i) => i === position ? normalize(value) === normalize(size) : value === selected[i]));
  return { exists: matches.length > 0, available: matches.some(v => v.available) };
}
export function recommendSize(rows, sizes, width, length, fit = 0, tolerance = 2) {
  const allowed = new Set(sizes.map(normalize));
  const candidates = rows.filter(r => allowed.has(normalize(r.size)));
  const target = width + fit;
  if (!Number.isFinite(width) || width <= 0 || !candidates.length || target < Math.min(...candidates.map(r => r.width)) - tolerance || target > Math.max(...candidates.map(r => r.width)) + tolerance) return null;
  const ranked = candidates.map(row => ({ row, score: Math.abs(row.width - target) + (length ? Math.abs(row.length - length) * .25 : 0) })).sort((a, b) => a.score - b.score || b.row.width - a.row.width);
  if (Math.abs(ranked[0].row.width - target) > Math.max(3, tolerance)) return null;
  return ranked[0].row;
}
export function resolveChartKey(product, data) {
  if (!product || !data?.charts) return null;
  const title = String(product.title || '').toLowerCase();
  const pt = String(product.productType || '').toLowerCase().trim();
  const sku = String(product.sku || '').toUpperCase();
  const images = Array.isArray(product.images) ? product.images : [];
  const isBox = pt.includes('überraschungsbox') || pt.includes('uberraschungsbox') || title.includes('überraschungsbox') || title.includes('uberraschungsbox');
  const isDamen = /\b(damen|lady|girl|frauen|bustier|leggings)\b|dament|damen-/.test(title);
  if (isDamen) {
    if (pt === 'hose' || pt === 'shorts' || /leggings|bra|crop|bustier/.test(title)) return null;
    if (pt === 't-shirt' || pt === 'longsleeve' || isBox) return sku.includes('3005') || images.some(im => String(im).includes('3005')) ? 'damen_tshirt_3005' : 'damen_tshirt_0201';
    if (pt === 'hoodie' || pt === 'jacke') return 'damen_hoodie_2112';
    if (pt.includes('muskelshirt') || pt.includes('tank')) return 'damen_tank_1051';
  }
  if (pt === 'sweatshirt') return 'sweatshirt';
  const byImg = data.byImage || {};
  for (const raw of images) {
    const clean = stripUuid(raw);
    const low = clean.toLowerCase();
    if (low.includes('10_damen_t-shirt_0201')) return 'damen_tshirt_0201';
    if (low.includes('10_damen_t-shirt_3005')) return 'damen_tshirt_3005';
    if (low.includes('13_damen_hoodie_2112')) return 'damen_hoodie_2112';
    if (low.includes('14_damen_lady_tank_1051')) return 'damen_tank_1051';
    if (low.includes('08_herren_sweatshirt') || low.includes('grossen-bb003-sweatshirt')) return 'sweatshirt';
    if (low.includes('03_herren_longsleeve') || low.includes('ls4099_grossentabelle')) return 'longsleeve';
    if (low.includes('05_herren_trainingsjacke') || low.includes('groessenbild-sweatjacke-5290')) return 'trainingsjacke_5290';
    if (low.includes('06_herren_bomberjacke')) return 'bomber_3150';
    if (low.includes('07_collegejacke') || low.includes('grossenbilder--cj')) return 'college';
    if (low.includes('09_herren_muskelshirt') || low.includes('grossenbilder--003')) return 'tank';
    if (low.includes('12_herren_shorts') || low.includes('grossenbilder-080')) return 'shorts';
    if (low.includes('15_herren_hemd') || low.includes('grossenbild-4024') || low.includes('gr-ssenbilder-vintage-hemden')) return 'vintage';
    if (low.includes('16_herren_holzfaellerweste') || low.includes('gr-ssenbilder-ranger')) return 'weste_4034';
    if (low.includes('04_herren_basic_hoodie') || low.includes('bybb005_g202102')) return 'hoodie';
    if (low.includes('04_herren_hoodie_443') || low.includes('groessen-bb011-hoodie') || low.includes('by011_g202505')) return 'hoodie_443';
    if (low.includes('02_herren_kapuzenjacke') || low.includes('kj707_grossentabelle') || low.includes('groessen-bb012-zipper') || low.includes('groessenbild-sr-zipper-444')) return 'jacket';
    if (low.includes('grossenbilder--zipper-2021-hrm')) {
      if (pt === 'hoodie') return /(0001|BY001|BB01)/.test(sku) ? 'hoodie' : 'hoodie_443';
      return 'jacket';
    }
    if ((low.includes('11_herren_jogginghose') || low.includes('grosse-gb5v2-002') || low.includes('groesse-hose-002') || low.includes('gr-ssenbild-hose')) && pt !== 'jacke') return 'joggers';
    if (byImg[raw]) return byImg[raw];
    if (byImg[clean]) return byImg[clean];
    if (low.includes('01_herren_t-shirt') || low.includes('t-shirt-groessentabelle-2026') || low.includes('t-shirt_grossentabelle') || low.includes('t-shirt_gr_entabelle') || low.includes('gr-ssenbild-t-shirt') || low.includes('grossenbilder--2021') || low.includes('codex-bild_20._aug._2026')) return 'tshirtS';
  }
  if (pt === 't-shirt' || pt === 'bundle' || isBox) return 'tshirtS';
  if (pt === 'longsleeve') return 'longsleeve';
  if (pt === 'hemd') return 'vintage';
  if (pt === 'weste') return 'weste_4034';
  if (pt.includes('muskelshirt') || pt.includes('tank')) return 'tank';
  if (pt === 'shorts') return 'shorts';
  if (pt === 'hose') return title.includes('leggings') ? null : 'joggers';
  if (pt === 'hoodie') return /(0900|0011|BB011|B011|0443|443)/.test(sku) || title.includes('premium') ? 'hoodie_443' : 'hoodie';
  if (pt === 'jacke') {
    if (/(0015|0043)/.test(sku) || title.includes('college')) return 'college';
    if (/(5290|1001)/.test(sku) || title.includes('trainingsjacke') || title.includes('sweatjacke')) return 'trainingsjacke_5290';
    if (/(3150|3149)/.test(sku) || title.includes('bomber')) return 'bomber_3150';
    return 'jacket';
  }
  return null;
}
let chartRequest;
const loadCharts = url => chartRequest ||= fetch(url).then(r => { if (!r.ok) throw new Error('Charts unavailable'); return r.json(); }).catch(e => { chartRequest = null; throw e; });
const parseMeasure = value => /^\d+(?:[,.]\d{1,2})?$/.test(value.trim()) ? Number(value.trim().replace(',', '.')) : NaN;
class SizeFinder extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.product = JSON.parse(this.querySelector('[data-ss-product]').textContent);
    this.form = this.closest('.product-form');
    if (!this.form) return;
    const opts = { signal: this.abort.signal };
    this.form.addEventListener('change', () => { this.invalidate(); requestAnimationFrame(() => this.sync()); }, opts);
    this.form.addEventListener('click', event => {
      const label = event.target.closest('.radios--value-button');
      if (label?.closest('[data-ss-size]') && label.querySelector('input')?.disabled) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, { ...opts, capture: true });
    this.form.addEventListener('submit', event => {
      const selected = this.selected();
      const state = sizeAvailability(this.product.variants, selected, this.product.sizePosition, selected[this.product.sizePosition]);
      if (!state.available) { event.preventDefault(); event.stopImmediatePropagation(); this.selection.textContent = 'Diese Kombination ist nicht verfügbar. Bitte wähle eine verfügbare Größe.'; }
    }, { ...opts, capture: true });
    this.selection = this.querySelector('[data-ss-selection]');
    this.details = this.querySelector('details');
    this.result = this.querySelector('[data-ss-result]');
    this.apply = this.querySelector('[data-ss-apply]');
    this.querySelector('[data-ss-calculate]').addEventListener('click', () => this.calculate(), opts);
    this.apply.addEventListener('click', () => this.choose(), opts);
    this.details.addEventListener('input', () => this.invalidate(), opts);
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.form, { childList: true, subtree: true });
    this.sync();
    loadCharts(this.dataset.chartsUrl).then(data => {
      if (!this.isConnected) return;
      const key = resolveChartKey(this.product, data);
      if (!key || !data.charts[key]) return;
      const chart = data.charts[key];
      if (!this.product.sizes.some(s => chart.rows.some(r => normalize(r.size) === normalize(s)))) return;
      this.chart = chart;
      this.details.hidden = false;
      if (this.chart.kind === 'bottom') {
        this.querySelector('[data-ss-width-label]').textContent = 'Bundbreite flach gemessen (cm)';
        this.querySelector('[data-ss-length-label]').textContent = 'Außenbeinlänge (cm, optional)';
        this.querySelector('[data-ss-measure]').textContent = 'Halbe Bundweite am Kleidungsstück, ohne Dehnen; kein Körperumfang.';
      } else if (this.chart.lengthLabel) this.querySelector('[data-ss-length-label]').textContent = this.chart.lengthLabel + ' (cm, optional)';
      const missing = this.product.sizes.filter(s => !this.chart.rows.some(r => normalize(r.size) === normalize(s)));
      this.querySelector('[data-ss-coverage]').textContent = 'Maße aus der Tabelle dieses Produkts (' + this.chart.name + '). ' + (this.chart.tolerance ? 'Fertigungstoleranz bis ±' + this.chart.tolerance.toLocaleString('de-DE') + ' cm. ' : '') + (missing.length ? 'Für ' + missing.join(', ') + ' enthält die hinterlegte Tabelle keine Maße; dafür geben wir keine Empfehlung.' : '');
    }).catch(() => {});
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.abort = null; }
  selected() {
    const values = [...this.product.selected];
    this.form.querySelectorAll('.product-options--option').forEach(group => {
      const input = group.querySelector('input[type="radio"]:checked');
      if (input) values[Number(group.dataset.index)] = input.value;
    });
    return values;
  }
  sync() {
    const selected = this.selected();
    const group = this.form.querySelector('.product-options--option[data-index="' + this.product.sizePosition + '"]');
    if (!group) return;
    group.setAttribute('data-ss-size', '');
    group.querySelectorAll('input[type="radio"]').forEach(input => {
      const state = sizeAvailability(this.product.variants, selected, this.product.sizePosition, input.value);
      input.disabled = !state.available;
      const text = state.available ? input.value : input.value + (state.exists ? ' – Ausverkauft' : ' – Nicht angeboten');
      if (input.getAttribute('aria-label') !== text) input.setAttribute('aria-label', text);
      const label = input.closest('label');
      if (label && label.title !== text) label.title = text;
    });
    const size = selected[this.product.sizePosition];
    const state = sizeAvailability(this.product.variants, selected, this.product.sizePosition, size);
    const status = 'Gewählte Größe: ' + size + (state.available ? '' : state.exists ? ' – Ausverkauft. Bitte eine verfügbare Größe wählen.' : ' – Diese Kombination wird nicht angeboten.');
    if (this.selection.textContent !== status) this.selection.textContent = status;
    if (this.recommendation) this.updateAvailability();
  }
  invalidate() { this.recommendation = null; this.apply.hidden = true; if (this.result.textContent) this.result.textContent = ''; }
  calculate() {
    if (!this.chart) return;
    const widthField = this.querySelector('[data-ss-width]');
    const lengthField = this.querySelector('[data-ss-length]');
    const width = parseMeasure(widthField.value);
    const length = lengthField.value.trim() ? parseMeasure(lengthField.value) : null;
    if (!Number.isFinite(width) || width < 10 || width > 150 || (length !== null && (!Number.isFinite(length) || length < 20 || length > 180))) {
      this.invalidate(); this.result.textContent = 'Bitte gültige Kleidungsmaße in Zentimetern eingeben (z. B. 59 oder 59,5).'; widthField.focus(); return;
    }
    this.recommendation = recommendSize(this.chart.rows, this.product.sizes, width, length, Number(this.querySelector('[data-ss-fit]').value), this.chart.tolerance);
    this.result.replaceChildren();
    if (!this.recommendation) { this.apply.hidden = true; this.result.textContent = 'Für diese Maße können wir mit der hinterlegten Produkttabelle keine verlässliche Größe empfehlen.'; return; }
    const r = this.recommendation;
    const heading = document.createElement('strong'); heading.textContent = 'Unsere Empfehlung: ' + r.size;
    const dimensions = document.createElement('p');
    const fmt = value => value.toLocaleString('de-DE');
    dimensions.textContent = 'Kleidungsmaße: ' + fmt(r.width) + ' cm ' + (this.chart.kind === 'bottom' ? 'Bundbreite' : 'halbe Oberweite') + ', ' + fmt(r.length) + ' cm ' + (this.chart.lengthLabel || (this.chart.kind === 'bottom' ? 'Außenbeinlänge' : 'Länge')) + (r.sleeve ? ', ' + fmt(r.sleeve) + ' cm Ärmel.' : '.');
    this.result.append(heading, dimensions);
    if (length && Math.abs(r.length - length) > 4) { const note = document.createElement('p'); note.textContent = 'Bitte die Länge beachten: Sie weicht um ' + fmt(Math.abs(r.length - length)) + ' cm von deinem Vergleichsstück ab.'; this.result.append(note); }
    const availability = document.createElement('p'); availability.dataset.ssAvailability = ''; this.result.append(availability);
    this.updateAvailability();
  }
  updateAvailability() {
    const state = sizeAvailability(this.product.variants, this.selected(), this.product.sizePosition, this.recommendation.size);
    const target = this.result.querySelector('[data-ss-availability]');
    const message = state.available ? 'Für die gewählte Farbe und Ausführung verfügbar.' : state.exists ? 'Diese Größe ist für die gewählte Farbe und Ausführung ausverkauft.' : 'Diese Größe wird in der gewählten Farbe und Ausführung nicht angeboten.';
    if (target && target.textContent !== message) target.textContent = message;
    this.apply.hidden = !state.available;
    this.apply.disabled = !state.available;
    const buttonText = 'Größe ' + this.recommendation.size + ' wählen';
    if (this.apply.textContent !== buttonText) this.apply.textContent = buttonText;
  }
  choose() {
    if (!this.recommendation) return;
    const group = this.form.querySelector('[data-ss-size]');
    const input = [...group.querySelectorAll('input[type="radio"]')].find(i => normalize(i.value) === normalize(this.recommendation.size));
    if (!input || input.disabled) return;
    input.click(); this.details.open = false; input.focus();
  }
}
if (typeof customElements !== 'undefined' && !customElements.get('ss-size-finder')) customElements.define('ss-size-finder', SizeFinder);
